import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import NavBarNB from '../../components/NavBarNB';
import FooterNB from '../../components/FooterNB';
import CardNB from '../../components/ui-nb/CardNB';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import BadgeNB from '../../components/ui-nb/BadgeNB';
import IconNB from '../../components/ui-nb/IconNB';
import StatusTimelineClient from '../../components/client/StatusTimelineClient';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { useTranslation } from '../../i18n';

interface StatusHistoryEntry {
  id: string;
  status_id: string;
  created_at: string;
  notes: string | null;
}

interface OrderPhoto {
  id: string;
  url: string;
  type: string;
  created_at: string;
}

interface StatusConfigEntry {
  label: string;
  position: number;
}

interface Order {
  id: string;
  release_code: string;
  device: string;
  device_brand: string | null;
  device_model: string | null;
  failure_description: string | null;
  created_at: string;
  estimated_date: string | null;
  status: string;
  status_history: StatusHistoryEntry[];
  order_photos: OrderPhoto[];
  branch: { status_config: Record<string, StatusConfigEntry> | null } | null;
}

interface PartRequest {
  id: string;
  description: string;
  cost: number;
  status: string;
}

const STATUS_CONFIG: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info' }> = {
  received: { label: 'Received', variant: 'info' },
  'in-repair': { label: 'In repair', variant: 'warning' },
  ready: { label: 'Ready for delivery', variant: 'success' },
};

export default function ClientEquipmentDetail() {
  const { t } = useTranslation();
  const { code } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [pendingPart, setPendingPart] = useState<PartRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;

    async function fetchOrder() {
      if (!isSupabaseConfigured()) {
        setError('Database configuration not available');
        setLoading(false);
        return;
      }

      try {
        const { data: orderData, error: orderError } = await supabase
          .rpc('get_public_order_by_code', { p_release_code: code })
          .single();

        if (orderError) {
          setError('Order not found');
          setLoading(false);
          return;
        }

        if (orderData) {
          const data = orderData as Record<string, unknown>;
          const mappedOrder: Order = {
            id: data.id as string,
            release_code: data.release_code as string,
            device: (data.device as string) || 'Unknown device',
            device_brand: data.device_brand as string | null,
            device_model: data.device_model as string | null,
            failure_description: data.failure_description as string | null,
            created_at: data.created_at as string,
            estimated_date: data.estimated_date as string | null,
            status: data.status as string,
            status_history: (data.status_history as StatusHistoryEntry[]) || [],
            order_photos: ((data.order_photos as OrderPhoto[]) || []).filter((p: OrderPhoto) => p.type === 'progress'),
            branch: data.branch as Order['branch'],
          };
          setOrder(mappedOrder);

          if (mappedOrder.status === 'in-repair') {
            const { data: partData } = await supabase
              .from('part_request')
              .select('*')
              .eq('order_id', mappedOrder.id)
              .eq('status', 'pending')
              .single();

            if (partData) {
              setPendingPart(partData);
            }
          }
        }
      } catch {
        setError('Error loading order');
      } finally {
        setLoading(false);
      }
    }

    fetchOrder();
  }, [code]);

  const currentStatusConfig = order ? (STATUS_CONFIG[order.status] || STATUS_CONFIG['received']) : STATUS_CONFIG['received'];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--nb-bg-page)', display: 'flex', flexDirection: 'column' }}>
        <NavBarNB />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="text-center">
            <div style={{ width: 32, height: 32, border: '3px solid #000000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ color: '#787878' }}>{t('clientEquipment.loading')}</p>
          </div>
        </main>
        <FooterNB />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--nb-bg-page)', display: 'flex', flexDirection: 'column' }}>
        <NavBarNB />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="text-center max-w-md">
            <CardNB className="mb-6" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 64, height: 64, padding: 0, margin: '0 auto 16px' }}>
              <IconNB name="close" size={32} />
            </CardNB>
            <h1 className="text-2xl font-bold text-black mb-2">{t('clientEquipment.equipmentNotFound')}</h1>
            <p className="mb-6 text-nb-text-secondary">{error || t('clientEquipment.notFoundMessage')}</p>
            <Link to="/track">
              <ButtonNB variant="primary" size="md">
                {t('clientEquipment.backToHome')}
              </ButtonNB>
            </Link>
          </div>
        </main>
        <FooterNB />
      </div>
    );
  }

  const progressPhotos = order.order_photos.filter((p) => p.type === 'progress');

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--nb-bg-page)', display: 'flex', flexDirection: 'column' }}>
      <NavBarNB />

      <main style={{ flex: 1, maxWidth: 896, width: '100%', margin: '0 auto', padding: '32px 16px' }}>
        <div className="flex items-center justify-between mb-6">
          <Link
            to={`/track?code=${code}`}
            style={{ color: '#000000', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            <IconNB name="chevronLeft" size={16} />
            {t('clientEquipment.backToHome')}
          </Link>
          <Link
            to="/track"
            style={{ color: '#000000', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            {t('tracking.backToTracking') || 'Track another repair'}
            <IconNB name="chevronRight" size={16} />
          </Link>
        </div>

        {pendingPart && (
          <CardNB className="mb-6" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <IconNB name="sun" size={24} />
              <div>
                <p className="font-semibold text-black">{t('clientEquipment.partRequired')}</p>
                <p className="text-sm text-nb-text-secondary">{t('clientEquipment.partRequiredDesc')}</p>
              </div>
            </div>
            <Link to={`/en/client/approve-part/${pendingPart.id}`}>
              <ButtonNB variant="primary" size="sm">
                {t('clientEquipment.reviewPart')}
              </ButtonNB>
            </Link>
          </CardNB>
        )}

        <CardNB className="mb-6">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h1 className="text-2xl font-bold text-black">
                {order.device_brand || ''} {order.device_model || order.device || 'Unknown'}
              </h1>
              <p className="text-sm mt-1 text-nb-text-muted">Folio: {order.release_code}</p>
            </div>
            <BadgeNB variant={currentStatusConfig.variant}>
              {currentStatusConfig.label}
            </BadgeNB>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, fontSize: 14 }}>
            <div>
              <p className="text-nb-text-muted">{t('clientEquipment.equipmentType')}</p>
              <p className="font-medium text-black">{order.device}</p>
            </div>
            <div>
              <p className="text-nb-text-muted">{t('clientEquipment.brandModel')}</p>
              <p className="text-black">{order.device_brand || '-'} {order.device_model || ''}</p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p className="text-nb-text-muted">{t('clientEquipment.problemDescription')}</p>
              <p className="text-black">{order.failure_description || '-'}</p>
            </div>
            <div>
              <p className="text-nb-text-muted">{t('clientEquipment.receptionDate')}</p>
              <p className="text-black">{order.created_at}</p>
            </div>
            <div>
              <p className="text-nb-text-muted">{t('clientEquipment.estimatedDelivery')}</p>
              <p className="text-black">{order.estimated_date || '-'}</p>
            </div>
          </div>
        </CardNB>

        {progressPhotos.length > 0 && (
          <CardNB className="mb-6">
            <h2 className="text-lg font-semibold text-black mb-4">{t('clientEquipment.progressPhotos')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 16 }}>
              {progressPhotos.map((photo) => (
                <div
                  key={photo.id}
                  style={{ aspectRatio: '1', backgroundColor: 'var(--nb-bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#787878', fontSize: 12, border: '2px solid #000' }}>
                  <img
                    src={photo.url}
                    alt="Progress"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              ))}
            </div>
          </CardNB>
        )}

        <CardNB>
          <h2 className="text-lg font-semibold text-black mb-4">{t('clientEquipment.yourStatus')}</h2>
          <StatusTimelineClient
            history={order.status_history}
            statusConfig={order.branch?.status_config || null}
            isLoading={false}
          />
        </CardNB>
      </main>

      <FooterNB />
    </div>
  );
}